import { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, Stack, Typography, TextField, Chip, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Autocomplete, InputAdornment,
} from '@mui/material';
import {
  Savings as PrepayIcon, Search as SearchIcon, Info as InfoIcon,
  Receipt as ReceiptIcon, History as HistoryIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import PrepaymentForm from './PrepaymentForm';
import API from '../../BaseURL';

// ═══════════════════════════════════════════════════════════════════
//  The accounts desk version: search for a student, then take the
//  payment. Same form as the dialog that opens off a learner's row in
//  Approve Collections — this one just has to find the learner first.
// ═══════════════════════════════════════════════════════════════════

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const money = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const prettyType = (t) => String(t || '')
  .replace(/_+$/, '').replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const longDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || '').slice(0, 10))
  ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined,
      { day: 'numeric', month: 'long', year: 'numeric' })
  : '—';

export default function PrepaymentDeskPage() {
  const [students, setStudents] = useState([]);
  const [student, setStudent] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    API.get('/collections/students')
      .then(r => setStudents(Array.isArray(r.data) ? r.data : []))
      .catch(() => setStudents([]));
  }, []);

  const loadRecent = useCallback(() => {
    API.get('/daily-subscriptions/prepayments')
      .then(r => setRecent(Array.isArray(r.data) ? r.data.slice(0, 10) : []))
      .catch(() => setRecent([]));
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1180, mx: 'auto' }}>
      <ModuleHeader
        icon={<PrepayIcon />}
        title="Advance Payment"
        subtitle="A term, month or year paid up front at the desk — never through a teacher's round"
      />

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.6} alignItems="flex-start">

        <Card elevation={0} sx={{
          p: 2, borderRadius: 3, flex: '1 1 520px',
          bgcolor: '#fff', border: '1px solid #e3eaf3',
        }}>
          <Autocomplete
            options={students}
            value={student}
            onChange={(_, v) => setStudent(v)}
            getOptionLabel={(o) => `${o.name || ''} — ${o.index_no || ''}`}
            isOptionEqualToValue={(a, b) => a.index_no === b.index_no}
            renderInput={(p) => (
              <TextField {...p} size="small" label="Student"
                placeholder="Search by name or index number"
                InputProps={{ ...p.InputProps, startAdornment: (
                  <>
                    <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                    {p.InputProps.startAdornment}
                  </>
                ) }} />
            )}
            sx={{ mb: 2 }} />

          {!student ? (
            <Box sx={{ py: 6, textAlign: 'center', color: '#94a3b8' }}>
              <PrepayIcon sx={{ fontSize: 40, opacity: .4 }} />
              <Typography sx={{ fontSize: 13, mt: 1 }}>
                Choose a student to see what the period costs.
              </Typography>
              <Typography sx={{ fontSize: 11, mt: 0.5 }}>
                You can also take one straight off a learner's row in Approve Collections.
              </Typography>
            </Box>
          ) : (
            <PrepaymentForm
              studentId={student.index_no}
              studentName={student.name}
              className={student.class_name}
              onDone={() => { setStudent(null); loadRecent(); }} />
          )}
        </Card>

        <Card elevation={0} sx={{
          p: 1.7, borderRadius: 3, flex: '1 1 300px',
          bgcolor: '#f2f7fd', border: '1px solid #dce7f5',
        }}>
          <Stack direction="row" spacing={1.3}>
            <InfoIcon sx={{ fontSize: 19, color: '#2f6fb0', mt: 0.1 }} />
            <Typography sx={{ fontSize: 11.5, color: '#475569', lineHeight: 1.8 }}>
              • Posts to the cash book <b>immediately</b> and issues a receipt. It does not
              go to a teacher, and needs no approval — this desk is the approval.<br />
              • Anything owed is <b>cleared first</b>; the rest is held on account.<br />
              • The teacher then sees the student as <b>Paid ahead</b> with the row locked,
              so nobody can collect from them again.<br />
              • Days are counted as <b>school days</b> for that collection, so weekends and
              non-collection days are never charged for.<br />
              • Days already covered by money on account are <b>not quoted again</b>.
            </Typography>
          </Stack>
        </Card>
      </Stack>

      {recent.length > 0 && (
        <Card elevation={0} sx={{
          mt: 1.6, borderRadius: 3, overflow: 'hidden',
          bgcolor: '#fff', border: '1px solid #e3eaf3',
        }}>
          <Stack direction="row" spacing={1.2} alignItems="center"
            sx={{ px: 1.8, py: 1.3, borderBottom: '1px solid #eef2f7' }}>
            <HistoryIcon sx={{ fontSize: 19, color: '#64748b' }} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#1e3a6b' }}>
              Taken lately
            </Typography>
          </Stack>
          <TableContainer>
            <Table size="small" sx={{
              '& th': { bgcolor: '#f3f7fc', color: '#334a6b', fontWeight: 800,
                        fontSize: 10.5, py: 0.8 },
              '& td': { fontSize: 11.5, py: 0.5, borderBottom: '1px solid #eef2f7' },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Collection</TableCell>
                  <TableCell align="center">Days</TableCell>
                  <TableCell>Covers to</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Receipt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a' }}>
                        {r.student_name || r.student_id}
                      </Typography>
                      <Typography sx={{ fontSize: 9.5, color: '#94a3b8' }}>
                        {r.class_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{prettyType(r.income_type)}</TableCell>
                    <TableCell align="center" sx={TNUM}>{r.school_days}</TableCell>
                    <TableCell>{longDate(r.covers_to)}</TableCell>
                    <TableCell align="right" sx={{ ...TNUM, fontWeight: 700 }}>
                      {money(r.amount)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={r.transaction_id || ''}>
                        <Chip size="small" icon={<ReceiptIcon sx={{ fontSize: 13 }} />}
                          label={r.receipt_no || '—'}
                          sx={{ height: 20, fontSize: 9.5, fontWeight: 700 }} />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}
